import { cn } from "@/lib/utils";
import { getAuthorityStyle } from "./authority-colors";
import { HelpCircle, Palette } from "lucide-react";

export interface ResolvedCitationData {
  sourceType: string;
  sourceId: string;
  sourceName: string;
  authorityLevel: string;
  contentPreview: string;
  characterization?: string | null;
  sourceCategory?: string | null;
}

interface CitationLinkProps {
  sourceId: string;
  sourceType: string;
  resolved: ResolvedCitationData | undefined;
  onClick: (sourceType: string, sourceId: string) => void;
  className?: string;
}

export function CitationLink({
  sourceId: _sourceId,
  sourceType,
  resolved,
  onClick,
  className,
}: CitationLinkProps) {
  if (!resolved) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground",
          className,
        )}
      >
        <HelpCircle className="h-3 w-3" />
        <span className="opacity-60">{sourceType}</span>
      </span>
    );
  }

  const isDesignSystem = resolved.sourceCategory === "design_system";
  const style = getAuthorityStyle(resolved.authorityLevel);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(resolved.sourceType, resolved.sourceId);
      }}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors",
        "hover:ring-1 hover:ring-current/20 cursor-pointer",
        isDesignSystem ? "bg-primary/10 text-primary-ink" : style.bg,
        !isDesignSystem && style.text,
        className,
      )}
    >
      {isDesignSystem ? (
        <Palette className="h-3 w-3 shrink-0" />
      ) : (
        <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", style.dot)} />
      )}
      <span className="max-w-[180px] truncate">
        {isDesignSystem ? "Design System" : style.label} · {resolved.sourceName}
      </span>
    </button>
  );
}
